import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlagCheckered, faLock } from '@fortawesome/free-solid-svg-icons';
import { utcToLocalLabel } from '../utils/dateUtils';
import { clampTooltipX } from '../utils/tooltipClamp';

interface Props {
  cycleStart:  string;  // start_at_utc (UTC, "YYYY-MM-DD HH:MM:SS")
  deadline:    string;  // workout_deadline_utc (UTC)
  onViewDuel:  () => void;
  /**
   * testMode = true  → Button immer klickbar (für Entwicklung)
   * testMode = false → Button erst nach Deadline klickbar
   */
  testMode?: boolean;
  /**
   * Status des Matchups aus der DB (scheduled|dueling|completed)
   * Button wird erst freigeschaltet wenn status=completed
   */
  matchupStatus?: string;
}

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function toCountdown(ms: number): Countdown {
  const total   = Math.max(0, ms);
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours   = Math.floor((total / 1000 / 60 / 60) % 24);
  const days    = Math.floor(total / 1000 / 60 / 60 / 24);
  return { days, hours, minutes, seconds };
}

function pad(n: number): string { return String(n).padStart(2, '0'); }

/** "YYYY-MM-DD HH:MM:SS" → UTC milliseconds */
function utcMs(s: string): number {
  return new Date(s.replace(' ', 'T') + 'Z').getTime();
}

/**
 * Erzeugt den Text "Sunday at 8:00 PM" (oder ähnlich) in der lokalen Zeitzone des Users.
 * Verwendet immer 18:00 UTC als Anzeige-Zeit (duel_evaluate Zeitpunkt).
 */
function getUnlockText(_deadline: string): string {
  // Immer 18:00 UTC verwenden (duel_evaluate Zeitpunkt)
  const d = new Date(new Date().toISOString().split('T')[0] + 'T18:00:00Z');
  // Den Sonntag des aktuellen Zyklus finden basierend auf dem Deadline
  const deadlineDate = new Date(_deadline.replace(' ', 'T') + 'Z');
  // Nächsten Sonntag finden (Deadline ist immer Sonntag)
  d.setUTCFullYear(deadlineDate.getUTCFullYear(), deadlineDate.getUTCMonth(), deadlineDate.getUTCDate());
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = dayNames[d.getDay()];
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  const minStr = minutes > 0 ? `:${pad(minutes)}` : '';
  return `${day} at ${h12}${minStr} ${ampm}`;
}

/** Gibt eine Uhrzeit in lokaler Zeitzone zurück, z.B. "8:00 AM" */
function getLocalTime(utcHours: number, utcMinutes: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHours, utcMinutes));
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  const minStr = minutes > 0 ? `:${pad(minutes)}` : '';
  return `${h12}${minStr} ${ampm}`;
}

/**
 * Erzeugt "Monday at 6:00 AM" oder "Sunday at 8:00 PM" aus einem UTC-String.
 * Wochentag kommt aus dem UTC-String, Uhrzeit wird in lokale Zeitzone umgerechnet.
 * @param utcStr "YYYY-MM-DD HH:MM:SS" UTC
 * @param utcHours Die UTC-Stunden für die Anzeige (z.B. 4 für 04:00 UTC)
 */
function getLocalWeekdayTime(utcStr: string, utcHours: number): string {
  // Wochentag aus dem UTC-Datum (z.B. Sonntag für deadline)
  const base = new Date(utcStr.replace(' ', 'T') + 'Z');
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = dayNames[base.getUTCDay()];

  // Lokale Uhrzeit für die angegebene UTC-Stunde
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), utcHours, 0));
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  const minStr = minutes > 0 ? `:${pad(minutes)}` : '';
  return `${day} at ${h12}${minStr} ${ampm}`;
}

const DAYS_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export default function DuelCountdownButton({ cycleStart, deadline, onViewDuel, testMode = false, matchupStatus }: Props) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const startMs    = utcMs(cycleStart);
  const deadlineMs = utcMs(deadline);
  const remainMs   = deadlineMs - now;

  const isPast    = now >= deadlineMs;
  const evaluated = matchupStatus === 'completed';
  const isActive  = (isPast && evaluated) || testMode;

  const cd = toCountdown(remainMs);

  // Welcher Wochentag gerade ist (0=Mo, 6=So), basierend auf Zyklusstart
  const currentDayIndex = Math.min(6, Math.max(0, Math.floor((now - startMs) / 86400000)));

  return (
    <div className="duel-countdown-wrap">
      <button
        className={`duel-unlock-btn${isActive ? ' duel-unlock-btn--active' : ''}`}
        onClick={isActive ? onViewDuel : undefined}
        title={testMode && !isPast ? 'Test mode: button unlocked' : undefined}
      >
        {/* 7-Segment Batterie */}
        <div className="duel-week-segments">
          {DAYS_SHORT.map((day, i) => {
            let state: 'done' | 'current' | 'future';
            if (isActive || isPast) state = 'done';
            else if (i < currentDayIndex) state = 'done';
            else if (i === currentDayIndex) state = 'current';
            else state = 'future';
            return (
              <div key={day} className={`duel-week-seg duel-week-seg--${state}`} />
            );
          })}
        </div>

        {/* Label */}
        <span className="duel-unlock-label">
          {isActive ? (
            <>Show Duel Result</>
          ) : isPast && !evaluated ? (
            <>
              <FontAwesomeIcon icon={faLock} />
              {' '}Auswertung läuft…
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faLock} />
              {' '}Duel Result in: {cd.days > 0 ? `${cd.days}d ` : ''}{pad(cd.hours)}:{pad(cd.minutes)}:{pad(cd.seconds)}
            </>
          )}
        </span>
      </button>
    </div>
  );
}
